import type { SourceDepthLevel, SourceDepthSummary } from '../api/apiClient'

type SourceDepthBadgeProps = {
  depth?: SourceDepthSummary | null
  variant?: 'compact' | 'card' | 'panel'
  className?: string
}

const LEVEL_ORDER: SourceDepthLevel[] = ['foundation', 'referenced', 'developed', 'deep_archive']

const LEVEL_LABELS: Record<SourceDepthLevel, string> = {
  foundation: 'Foundation',
  referenced: 'Referenced',
  developed: 'Developed',
  deep_archive: 'Deep Archive',
}

const FALLBACK_DEPTH: SourceDepthSummary = {
  level: 'foundation',
  label: LEVEL_LABELS.foundation,
  context_count: 0,
  source_count: 0,
  source_types: [],
  corpus_lanes: [],
  has_primary_sources: false,
  has_external_urls: false,
  has_publication_dates: false,
  tooltip: 'Foundation: persona and site profile.',
}

const normalizeDepth = (depth?: SourceDepthSummary | null): SourceDepthSummary => {
  if (!depth || !LEVEL_ORDER.includes(depth.level)) return FALLBACK_DEPTH
  return {
    ...FALLBACK_DEPTH,
    ...depth,
    label: depth.label || LEVEL_LABELS[depth.level],
  }
}

const SourceDepthBadge = ({ depth, variant = 'card', className }: SourceDepthBadgeProps) => {
  const resolved = normalizeDepth(depth)
  const activeCount = LEVEL_ORDER.indexOf(resolved.level) + 1
  const title = resolved.tooltip || `${resolved.label}: source depth indicator.`
  const sourceText =
    resolved.context_count > 0
      ? `${resolved.source_count} source ${resolved.source_count === 1 ? 'group' : 'groups'}`
      : 'Profile base'

  const classes = [
    'source-depth-badge',
    `source-depth-badge--${resolved.level}`,
    `source-depth-badge--variant-${variant}`,
    className,
  ].filter(Boolean).join(' ')

  return (
    <span className={classes} title={title} aria-label={title}>
      <span className="source-depth-dots" aria-hidden="true">
        {LEVEL_ORDER.map((level, index) => (
          <span
            key={level}
            className={`source-depth-dot${index < activeCount ? ' active' : ''}`}
          />
        ))}
      </span>
      <span className="source-depth-copy">
        <span className="source-depth-label">{resolved.label}</span>
        <span className="source-depth-meta">{sourceText}</span>
        {variant === 'panel' && resolved.source_types && resolved.source_types.length > 0 && (
          <span className="source-depth-detail">{resolved.source_types.slice(0, 3).join(' / ')}</span>
        )}
      </span>
    </span>
  )
}

export default SourceDepthBadge
